/**
 * Shared companion-agent connection state for the whole app.
 * Boot: 1 probe + up to 2 retries (3 total), then stop until page reload.
 * While connected: one ping per minute.
 * User actions (crop, insert, settings) ping once before each command.
 */
import { useSyncExternalStore } from "react";
import * as agent from "./creDesktopAgent";

const MAX_BOOT_ATTEMPTS = 3;
const BOOT_RETRY_DELAY_MS = 400;
const HEARTBEAT_MS = 60_000;

let connected = false;
let discoveryExhausted = false;
let monitorEnabled = false;
let bootStarted = false;
let heartbeatTimer = null;

let snapshot = { connected: false };
const listeners = new Set();

function publish() {
  snapshot = { connected };
  listeners.forEach((listener) => listener());
}

function subscribe(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function getSnapshot() {
  return snapshot;
}

function stopHeartbeat() {
  if (heartbeatTimer) {
    clearInterval(heartbeatTimer);
    heartbeatTimer = null;
  }
}

function startHeartbeat() {
  stopHeartbeat();
  if (!connected || !monitorEnabled) return;

  heartbeatTimer = setInterval(() => {
    void runHeartbeatPing();
  }, HEARTBEAT_MS);
}

async function runHeartbeatPing() {
  if (!monitorEnabled || !connected) return;

  const ok = await agent.ping();
  connected = ok;
  publish();

  if (ok) {
    startHeartbeat();
  } else {
    stopHeartbeat();
  }
}

function setDisconnected() {
  const wasConnected = connected;
  connected = false;
  publish();
  if (wasConnected) {
    stopHeartbeat();
  }
}

async function probeOnce({ force = false, fullScan = false } = {}) {
  const ok = await agent.ping({ force, fullScan });
  const wasConnected = connected;
  connected = ok;
  publish();

  if (ok && !wasConnected) {
    startHeartbeat();
  } else if (!ok && wasConnected) {
    stopHeartbeat();
  }

  return ok;
}

async function runBootSequence() {
  if (bootStarted || discoveryExhausted || !monitorEnabled) return;
  bootStarted = true;

  for (let attempt = 0; attempt < MAX_BOOT_ATTEMPTS; attempt++) {
    if (!monitorEnabled) return;

    const ok = await probeOnce({ force: true, fullScan: attempt === 0 });
    if (ok) {
      discoveryExhausted = false;
      publish();
      return;
    }

    if (attempt < MAX_BOOT_ATTEMPTS - 1) {
      await new Promise((resolve) => setTimeout(resolve, BOOT_RETRY_DELAY_MS));
    }
  }

  discoveryExhausted = true;
  publish();
}

export function startConnectionMonitor() {
  if (monitorEnabled) return;
  monitorEnabled = true;
  void runBootSequence();
}

export function stopConnectionMonitor() {
  monitorEnabled = false;
  stopHeartbeat();
  connected = false;
  publish();
}

/**
 * Ping localhost, update shared UI state, then run the command only if reachable.
 * @param {Record<string, unknown>} command
 * @param {string|null|undefined} bearerToken
 */
export async function sendAgentCommand(command, bearerToken) {
  if (!monitorEnabled) {
    startConnectionMonitor();
  }

  const reachable = await probeOnce({ force: true, fullScan: false });
  if (!reachable) {
    return null;
  }

  const resp = await agent.sendCommand(command, bearerToken);
  if (resp === null) {
    setDisconnected();
    return null;
  }

  return resp;
}

export function useAgentConnection() {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}
